import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Clock3 } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import type { OrganizationDisplaySettings } from '../../../../utils/api';

interface RootCauseStatusBannerProps {
  settings?: OrganizationDisplaySettings | null;
  className?: string;
}

const ROOT_CAUSE_BANNER_STORAGE_KEY = 'go-root-cause-banner-seen';

const formatChangedAt = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const RootCauseStatusBanner: React.FC<RootCauseStatusBannerProps> = ({ settings, className }) => {
  const changedAt = formatChangedAt(settings?.root_cause_classification_changed_at);
  const storageKey = useMemo(() => {
    const changeMarker = settings?.root_cause_classification_changed_at || 'disabled';
    return `${ROOT_CAUSE_BANNER_STORAGE_KEY}:${changeMarker}`;
  }, [settings?.root_cause_classification_changed_at]);
  const [isVisible, setIsVisible] = useState(false);
  const hasShownForStorageKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!settings) {
      hasShownForStorageKeyRef.current = null;
      setIsVisible(false);
      return;
    }

    if (settings.root_cause_classification_enabled) {
      try {
        for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
          const key = window.localStorage.key(index);
          if (key?.startsWith(`${ROOT_CAUSE_BANNER_STORAGE_KEY}:`)) {
            window.localStorage.removeItem(key);
          }
        }
      } catch {
        // Ignore storage cleanup issues and fall back to hidden state.
      }
      hasShownForStorageKeyRef.current = null;
      setIsVisible(false);
      return;
    }

    const hasShownForCurrentKey = hasShownForStorageKeyRef.current === storageKey;

    try {
      const seen = window.localStorage.getItem(storageKey);
      if (seen === 'true' && !hasShownForCurrentKey) {
        setIsVisible(false);
        return;
      }
      hasShownForStorageKeyRef.current = storageKey;
      window.localStorage.setItem(storageKey, 'true');
      setIsVisible(true);
    } catch {
      hasShownForStorageKeyRef.current = storageKey;
      setIsVisible(true);
    }
  }, [settings, storageKey]);

  if (!settings || settings.root_cause_classification_enabled || !isVisible) {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 shadow-sm',
        className
      )}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <AlertCircle className="h-4 w-4" />
          </span>
          <div className="space-y-1">
            <p className="text-sm font-medium">Root cause classification is disabled</p>
            <p className="text-sm text-amber-800">
              Historical root-cause data remains visible, but new records will not be classified.
            </p>
          </div>
        </div>
        {changedAt && (
          <div className="flex items-center gap-2 text-xs font-medium text-amber-800">
            <Clock3 className="h-3.5 w-3.5" />
            Disabled on {changedAt}
          </div>
        )}
      </div>
    </div>
  );
};

export default RootCauseStatusBanner;
